// v2 hedgehog — a proper urchin: a low cream-faced body wearing a full domed
// spine coat — two rings of grizzled quills plus a crown quill, tilted outward
// all over the mantle, not just in one plane — a pointed pale face with a black
// button nose, bead eyes and little round ears, and stubby legs tucked under the
// skirt. Two coats: brown-grey and dark peat. Ported 1:1 from age-of-mcp's
// v2/hedgehog.ts, using the library's shared `critter` (quadLegs) helper.
import * as THREE from "three";
import { defineModel, critter, type Part } from "../../lib";

const COATS = [
  { mantle: "#5e5040", quill: "#38301f", quillHi: "#8a7a58", face: "#d4b288", leg: "#6a5138" },
  { mantle: "#4a4034", quill: "#272118", quillHi: "#6e6248", face: "#c2a276", leg: "#544230" },
];

// Spine-coat layout: rings of quills around the mantle dome. `alpha` is the
// polar tilt from straight up, `n` the quill count, `off` a half-step so the
// rings interleave instead of stacking.
const RINGS = [
  { alpha: 0.55, n: 6, off: 0.5 },
  { alpha: 1.05, n: 8, off: 0 },
];

// Mantle dome centre / radius / squash — the quills sit on this surface.
const DOME = { x: -0.004, y: 0.026, r: 0.027, sx: 1.3, sy: 1.0, sz: 1.15 };

function hedgehogParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = critter.quadLegs({
    dx: 0.015, dz: 0.0135, bodyY: 0.019, rTop: 0.0038, rFoot: 0.003, color: c.leg,
  });
  parts.push(
    // pale under-body and cheeks the coat drapes over
    { geometry: new THREE.IcosahedronGeometry(0.022, 0), color: c.face,
      position: [0.012, 0.019, 0], scale: [1.35, 0.85, 1.0] },
    // the spine-coat mantle dome
    { geometry: new THREE.IcosahedronGeometry(DOME.r, 0), color: c.mantle,
      position: [DOME.x, DOME.y, 0], scale: [DOME.sx, DOME.sy, DOME.sz] },
    // pointed snout with a black button nose
    { geometry: new THREE.ConeGeometry(0.0115, 0.026, 5), color: c.face,
      position: [0.034, 0.0155, 0], rotation: [0, 0, -Math.PI / 2] },
    { geometry: new THREE.IcosahedronGeometry(0.0034, 0), color: "#1a140e",
      position: [0.047, 0.0155, 0] },
    // bead eyes
    { geometry: new THREE.IcosahedronGeometry(0.0028, 0), color: "#201810",
      position: [0.0335, 0.0235, 0.0078] },
    { geometry: new THREE.IcosahedronGeometry(0.0028, 0), color: "#201810",
      position: [0.0335, 0.0235, -0.0078] },
    // little round ears at the coat's brow edge
    { geometry: new THREE.IcosahedronGeometry(0.004, 0), color: c.face,
      position: [0.026, 0.0335, 0.0105] },
    { geometry: new THREE.IcosahedronGeometry(0.004, 0), color: c.face,
      position: [0.026, 0.0335, -0.0105] },
  );
  // The quill coat: rings of outward-tilted cones over the dome, colours
  // alternating dark/grizzled for the banded-spine look. rotation [0, -th,
  // -alpha] (z first, then y) points each cone's +y axis at azimuth `th`,
  // tilt `alpha` — i.e. straight out of the mantle.
  let q = 0;
  for (const ring of RINGS) {
    const sa = Math.sin(ring.alpha), ca = Math.cos(ring.alpha);
    for (let k = 0; k < ring.n; k++) {
      const th = ((k + ring.off) / ring.n) * Math.PI * 2;
      parts.push({
        geometry: new THREE.ConeGeometry(0.0042, 0.02, 4),
        color: q++ % 2 === 0 ? c.quill : c.quillHi,
        position: [
          DOME.x + DOME.sx * DOME.r * sa * Math.cos(th) * 0.92,
          DOME.y + DOME.sy * DOME.r * ca * 0.92,
          DOME.sz * DOME.r * sa * Math.sin(th) * 0.92,
        ],
        rotation: [0, -th, -ring.alpha],
      });
    }
  }
  // crown quill straight up
  parts.push({
    geometry: new THREE.ConeGeometry(0.0042, 0.02, 4), color: c.quill,
    position: [DOME.x, DOME.y + DOME.r * 0.92, 0],
  });
  return parts;
}

export const hedgehogV2 = defineModel({
  id: "hedgehog-v2",
  batchId: "fable",
  name: "Hedgehog (v2)",
  tags: ["creature","hedgehog","low-poly","age-of-mcp","fable"],
  summary: "Low-poly hedgehog.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.05,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => hedgehogParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => hedgehogParts(1) },
  ],
  animations: [],
});
